import React from "react";
import { useContent } from "@thoughtbot/superglue";

import {
  Form,
  type FormProps,
  type RailsEmailFieldProps,
  type RailsSubmitButtonProps,
} from "@javascript/components/Inputs";
import { AuthCard } from "@javascript/components/AuthCard";
import { AuthShell } from "@javascript/components/AuthShell";
import { Button } from "@javascript/components/ui/button";
import { Field, FieldError, FieldLabel } from "@javascript/components/ui/field";
import { Input } from "@javascript/components/ui/input";
import { TypographyH1, TypographyMuted } from "@javascript/components/ui/typography";

type ConfirmationContent = {
  confirmationForm: FormProps<{
    email: RailsEmailFieldProps;
    submit: RailsSubmitButtonProps;
  }>;
  errors: Record<string, string[]>;
  signInPath: string;
  brandMotifPath: string;
};

export default function Confirmation() {
  const { confirmationForm, errors, signInPath, brandMotifPath } = useContent<ConfirmationContent>();
  const { form, extras, inputs } = confirmationForm;
  const emailErrors = (errors.email ?? []).map((message) => ({ message }));

  return (
    <AuthShell brandMotifPath={brandMotifPath}>
      <AuthCard>
        <div className="space-y-2">
          <TypographyH1 className="text-left">Resend confirmation</TypographyH1>
          <TypographyMuted>Enter your email and we’ll send a fresh confirmation link.</TypographyMuted>
        </div>

        <Form {...form} extras={extras} className="mt-6 space-y-5">
          <Field data-invalid={emailErrors.length > 0}>
            <FieldLabel htmlFor={inputs.email.id}>Email</FieldLabel>
            <Input {...inputs.email} placeholder="you@example.com" aria-invalid={emailErrors.length > 0} />
            <FieldError errors={emailErrors} />
          </Field>
          <Button type="submit" className="h-12 w-full rounded-full text-base font-semibold shadow-sm">
            {inputs.submit.text}
          </Button>
        </Form>
      </AuthCard>

      {/* Outside the card on purpose: the card is the task, and this speaks
          about the account around it. */}
      <p
        data-auth-signin-prompt
        className="mt-5 flex flex-wrap items-baseline justify-between gap-x-4 gap-y-1 text-sm"
      >
        <span className="text-muted-foreground">Already confirmed?</span>
        <a
          href={signInPath}
          className="inline-flex min-h-11 items-center gap-1.5 whitespace-nowrap font-semibold text-foreground underline-offset-4 transition-colors hover:text-primary hover:underline focus-visible:rounded-sm focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
        >
          Sign in
          <span aria-hidden className="text-primary">
            →
          </span>
        </a>
      </p>
    </AuthShell>
  );
}
